import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ScanEye, MessagesSquare, BarChart3, Globe, Lock, ArrowRight } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import BuildBadge from "@/components/BuildBadge";
import AppStatusDot from "@/components/AppStatusDot";
import MadeBySSPL from "@/components/MadeBySSPL";

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" /> {children}
  </span>
);

const Pillar = ({ n, icon: Icon, title, body }) => (
  <div className="border border-border bg-card p-8 transition hover:-translate-y-0.5">
    <div className="flex items-center justify-between mb-8">
      <span className="label-mono text-muted-foreground">— {n}</span>
      <Icon className="w-5 h-5 text-primary" />
    </div>
    <h3 className="font-display text-2xl font-medium tracking-tight">{title}</h3>
    <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display text-base font-medium truncate">Sovereign VDR</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/login"
              data-testid="landing-login-btn"
              className="hidden sm:inline-flex px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              data-testid="landing-register-btn"
              className="hidden sm:inline-flex px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
            >
              Request access
            </Link>
            <ThemeToggle floating={false} />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient pointer-events-none" />
        <div className="absolute inset-0 sovereign-grid opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 pt-24 pb-32">
          <Pill>Project of Indo-Pacific Significance · Canberra · ACT</Pill>
          <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tighter max-w-4xl">
            A sovereign vault for your most sensitive transactions.
          </h1>
          <p className="mt-6 text-base text-muted-foreground max-w-2xl leading-relaxed">
            SovereignHPC&rsquo;s Virtual Data Room for M&amp;A, fundraising, and
            due-diligence — engineered with granular permissions, fence-view
            watermarking, MFA, and an integrated Q&amp;A module.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-accent text-accent-foreground text-sm font-medium hover:translate-y-[-1px] transition"
            >
              Start a data room <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-border text-sm hover:bg-secondary transition"
            >
              Sign in to existing room
            </Link>
          </div>

          {/* metric strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-l border-border">
            {[
              ["100%", "Renewable Grid"],
              ["AU", "Data Sovereignty"],
              ["MFA", "Encrypted in transit"],
              ["7", "Permission Layers"],
            ].map(([k, v]) => (
              <div key={v} className="border-r border-b border-border p-6">
                <div className="font-display text-3xl tracking-tighter">{k}</div>
                <div className="label-mono text-muted-foreground mt-2">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-border overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-3 ticker text-muted-foreground">
          {Array(2).fill(0).map((_, i) => (
            <span key={`marquee-${i}`} className="flex">
              {"AI-EXASCALE / ZERO-CARBON / SOVEREIGN-CLOUD / INDO-PACIFIC / RENEWABLE-GRID / NATIONAL-RESILIENCE / GPU-FABRIC / REGIONAL-COLLAB / "
                .split(" / ").map((w) => (
                <span key={`m${i}-${w}`} className="px-6 border-r border-border">{w}</span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <Pill>01 / Capabilities</Pill>
            <h2 className="mt-6 font-display text-3xl lg:text-5xl font-medium tracking-tight max-w-2xl">
              Built for boardroom-grade due diligence.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-border">
          <Pillar
            n="01"
            icon={Lock}
            title="Granular permissions"
            body="Seven permission layers — Fence, View, Encrypted, PDF, Original, Upload, Manage — controllable per user, per group, per document."
          />
          <Pillar
            n="02"
            icon={ScanEye}
            title="Fence-view watermarking"
            body="User-identifying watermarks rendered over every document view. Right-click, print, and devtools shortcuts disabled to reduce data leakage."
          />
          <Pillar
            n="03"
            icon={ShieldCheck}
            title="MFA & encrypted transport"
            body="TOTP-based multi-factor authentication, encrypted-at-rest GridFS storage, and audited activity for compliance reporting."
          />
          <Pillar
            n="04"
            icon={MessagesSquare}
            title="Integrated Q&A module"
            body="Centralized question-and-answer threads keep buyers, sellers, and advisors aligned — no more lost email chains."
          />
          <Pillar
            n="05"
            icon={BarChart3}
            title="Page-by-page analytics"
            body="See who viewed what, when, and how long. Track signins, document views, and participant engagement over custom windows."
          />
          <Pillar
            n="06"
            icon={Globe}
            title="Custom branded rooms"
            body="Unlimited data rooms with per-room brand colors and watermark text. Designed for advisors managing parallel transactions."
          />
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-8 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="label-mono text-muted-foreground">— Encrypted in transit · Held in AU</div>
          <div className="text-xs text-muted-foreground">© {new Date().getFullYear()} SovereignHPC — Sovereign Data Launchpad™</div>
        </div>
        <div className="max-w-7xl mx-auto px-8 pb-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <AppStatusDot />
          <span className="hidden sm:inline text-muted-foreground/40">·</span>
          <BuildBadge align="center" />
          <span className="hidden sm:inline text-muted-foreground/40">·</span>
          <MadeBySSPL align="center" />
        </div>
      </footer>
    </div>
  );
}
